import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { albums, albumIndex } from '../data/albums';

const PlayerContext = createContext(null);

const LS_VOLUME = 'armagedify:volume';
const LS_MUTED  = 'armagedify:muted';
const LS_LIKES  = 'armagedify:likes';

const readLikes = () => {
  try {
    const raw = localStorage.getItem(LS_LIKES);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const readNum = (key, fallback) => {
  const raw = localStorage.getItem(key);
  if (raw == null) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
};

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(() => readNum(LS_VOLUME, 0.8));
  const [isMuted, setIsMuted] = useState(() => localStorage.getItem(LS_MUTED) === '1');
  const [likes, setLikes] = useState(readLikes);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [npsOpen, setNpsOpen] = useState(false);

  const currentAlbum = albums[currentIndex];

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;
    audio.muted = isMuted;
  }, [volume, isMuted]);

  useEffect(() => {
    localStorage.setItem(LS_VOLUME, String(volume));
  }, [volume]);

  useEffect(() => {
    localStorage.setItem(LS_MUTED, isMuted ? '1' : '0');
  }, [isMuted]);

  useEffect(() => {
    localStorage.setItem(LS_LIKES, JSON.stringify(likes));
  }, [likes]);

  const playAlbumById = useCallback((id) => {
    const idx = albumIndex(id);
    if (idx < 0) return;
    setCurrentIndex(idx);
    setIsPlaying(true);
  }, []);

  const togglePlay = useCallback(() => {
    setIsPlaying((p) => !p);
  }, []);

  const next = useCallback(() => {
    const nextIdx = (currentIndex + 1) % albums.length;
    setCurrentIndex(nextIdx);
    setIsPlaying(true);
    navigate(`/album/${albums[nextIdx].id}`);
  }, [currentIndex, navigate]);

  const prev = useCallback(() => {
    const audio = audioRef.current;
    if (audio && audio.currentTime > 3) {
      audio.currentTime = 0;
      setCurrentTime(0);
      return;
    }
    const prevIdx = (currentIndex - 1 + albums.length) % albums.length;
    setCurrentIndex(prevIdx);
    setIsPlaying(true);
    navigate(`/album/${albums[prevIdx].id}`);
  }, [currentIndex, navigate]);

  const seek = useCallback((seconds) => {
    const audio = audioRef.current;
    if (!audio) return;
    const t = Math.max(0, Math.min(seconds, audio.duration || duration || 0));
    audio.currentTime = t;
    setCurrentTime(t);
  }, [duration]);

  const setVolume = useCallback((v) => {
    const clamped = Math.max(0, Math.min(1, v));
    setVolumeState(clamped);
    if (clamped > 0 && isMuted) setIsMuted(false);
  }, [isMuted]);

  const toggleMute = useCallback(() => setIsMuted((m) => !m), []);

  const toggleLike = useCallback((albumId) => {
    setLikes((prev) => ({ ...prev, [albumId]: !prev[albumId] }));
  }, []);

  // Sync isPlaying → actual audio element
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      const p = audio.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => setIsPlaying(false));
      }
    } else {
      audio.pause();
    }
  }, [isPlaying, currentIndex]);

  // Reset scrubbing state when track changes
  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
  }, [currentIndex]);

  const openNowPlaying = useCallback(() => setNpsOpen(true), []);
  const closeNowPlaying = useCallback(() => setNpsOpen(false), []);

  const value = useMemo(() => ({
    audioRef,
    albums,
    currentAlbum,
    currentIndex,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    likes,
    isScrubbing,
    npsOpen,
    setCurrentTime,
    setDuration,
    setIsScrubbing,
    playAlbumById,
    togglePlay,
    next,
    prev,
    seek,
    setVolume,
    toggleMute,
    toggleLike,
    openNowPlaying,
    closeNowPlaying,
  }), [
    currentAlbum, currentIndex, isPlaying, currentTime, duration, volume,
    isMuted, likes, isScrubbing, npsOpen,
    playAlbumById, togglePlay, next, prev, seek, setVolume, toggleMute,
    toggleLike, openNowPlaying, closeNowPlaying,
  ]);

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used within a PlayerProvider');
  return ctx;
}

export function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
